import forge from "node-forge";

export interface LoadedCertificate {
  /** Non-extractable CryptoKey, importable only for RSASSA-PKCS1-v1_5/SHA-256 signing. */
  privateKey: CryptoKey;
  /** DER-encoded chain, signing certificate first, then intermediates. */
  certChainDer: Uint8Array[];
  commonName: string;
  notAfter: Date;
}

function arrayBufferToBinaryString(buf: ArrayBuffer): string {
  return forge.util.binary.raw.encode(new Uint8Array(buf));
}

function forgeAsn1ToUint8Array(asn1: forge.asn1.Asn1): Uint8Array {
  const der = forge.asn1.toDer(asn1).getBytes();
  return forge.util.binary.raw.decode(der);
}

function findMatchingCert(
  key: forge.pki.rsa.PrivateKey,
  certs: forge.pki.Certificate[],
): forge.pki.Certificate | undefined {
  return certs.find((cert) => {
    const pub = cert.publicKey as forge.pki.rsa.PublicKey;
    return (
      pub.n !== undefined &&
      pub.n.compareTo(key.n) === 0 &&
      pub.e.compareTo(key.e) === 0
    );
  });
}

function orderChain(
  leaf: forge.pki.Certificate,
  rest: forge.pki.Certificate[],
): forge.pki.Certificate[] {
  const chain = [leaf];
  const remaining = [...rest];
  let current = leaf;
  for (;;) {
    const nextIndex = remaining.findIndex(
      (c) => c.subject.hash === current.issuer.hash,
    );
    if (nextIndex === -1) break;
    const [next] = remaining.splice(nextIndex, 1);
    chain.push(next);
    current = next;
  }
  return chain;
}

/**
 * Parses a .p12/.pfx file, imports the private key into WebCrypto as
 * non-extractable, and returns the certificate chain. Leaves no
 * extractable copy of the private key behind: the forge key material and
 * the intermediate PKCS#8 DER buffer are overwritten before returning.
 */
export async function loadP12(
  fileBuffer: ArrayBuffer,
  passphrase: string,
): Promise<LoadedCertificate> {
  const binaryDer = arrayBufferToBinaryString(fileBuffer);
  const asn1 = forge.asn1.fromDer(binaryDer);
  const p12 = forge.pkcs12.pkcs12FromAsn1(asn1, false, passphrase);

  const keyBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag })[
    forge.pki.oids.pkcs8ShroudedKeyBag
  ];
  const certBags = p12.getBags({ bagType: forge.pki.oids.certBag })[
    forge.pki.oids.certBag
  ];
  if (!keyBags || keyBags.length === 0) {
    throw new Error("no private key found in the .p12 file");
  }
  if (!certBags || certBags.length === 0) {
    throw new Error("no certificates found in the .p12 file");
  }

  const forgeKey = keyBags[0].key as forge.pki.rsa.PrivateKey;
  const certs = certBags
    .map((bag) => bag.cert)
    .filter((c): c is forge.pki.Certificate => c !== undefined);

  const leaf = findMatchingCert(forgeKey, certs);
  if (!leaf) {
    throw new Error("no certificate in the .p12 matches the private key");
  }
  const orderedCerts = orderChain(
    leaf,
    certs.filter((c) => c !== leaf),
  );

  const pkcs1Asn1 = forge.pki.privateKeyToAsn1(forgeKey);
  const pkcs8Asn1 = forge.pki.wrapRsaPrivateKey(pkcs1Asn1);
  const pkcs8Der = forgeAsn1ToUint8Array(pkcs8Asn1);

  let privateKey: CryptoKey;
  try {
    privateKey = await crypto.subtle.importKey(
      "pkcs8",
      pkcs8Der as BufferSource,
      { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
      false,
      ["sign"],
    );
  } finally {
    pkcs8Der.fill(0);
    forgeKey.n = forgeKey.e = forgeKey.d = null as unknown as forge.jsbn.BigInteger;
  }

  const commonName =
    leaf.subject.getField("CN")?.value ?? leaf.subject.attributes[0]?.value ?? "";

  return {
    privateKey,
    certChainDer: orderedCerts.map((c) =>
      forgeAsn1ToUint8Array(forge.pki.certificateToAsn1(c)),
    ),
    commonName,
    notAfter: leaf.validity.notAfter,
  };
}
